import { Timer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import revlvLogo from '../assets/revlv-logo.png';
import { Wordmark } from '../components/Wordmark';
import { clearCooldown, cooldownRemaining } from '../auth/signInCooldown';

/**
 * The wait after three failed sign-ins, with the way back drawn as it counts down.
 *
 * **This page is a COURTESY, not the control.** The server refuses the request whether or not
 * anybody visits — `AuthService` counts the window off `LoginAttempts` before it even looks the
 * account up, and a caller posting straight to `/api/auth/login` meets the same 429. If this
 * page were the enforcement it would be no enforcement at all, which is why the countdown is
 * free to be reloaded, bookmarked or closed.
 *
 * What it is FOR is the thing a bare error message could not do: say how long, out loud, and
 * take the reader back by itself. The sentence "try again in 47 seconds" stops being true the
 * moment it is read.
 */
export function SignInCooldown() {
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState(cooldownRemaining);

  /*
    Counted off a stored DEADLINE, never off a number ticking down in state.

    A tick-per-second counter is wrong in both directions here: a background tab is throttled
    so the wait runs long, and a reload would start the minute again from the top — which
    would make refreshing the page a punishment. Re-reading the deadline each tick makes the
    display a function of the clock, so it is correct however the tab has been treated.
  */
  useEffect(() => {
    if (remaining <= 0) {
      return;
    }

    const id = window.setInterval(() => setRemaining(cooldownRemaining()), 250);

    return () => window.clearInterval(id);
  }, [remaining]);

  useEffect(() => {
    if (remaining > 0) {
      return;
    }

    clearCooldown();
    navigate('/login', { replace: true });
  }, [remaining, navigate]);

  // Arriving with nothing to wait for — a bookmark, a back button, a second tab that already
  // served its wait. There is no page to draw, so do not flash one.
  if (remaining <= 0) {
    return <Navigate to="/login" replace />;
  }

  const total = 60;
  const elapsed = Math.min(Math.max(total - remaining, 0), total);

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-panel bg-brand-500 shadow-raised">
            <Timer className="h-6 w-6 text-white" aria-hidden="true" />
          </div>
          <h1 className="text-lg font-semibold text-slate-800"><Wordmark /></h1>
        </div>

        <div className="flex flex-col gap-5 rounded-panel border border-slate-200 bg-white p-6 shadow-panel">
          {/*
            The figure is the page. It is mono and tabular so the seconds do not shift the
            layout as they tick, and large because it is the one thing the reader came for.
          */}
          <div className="flex flex-col items-center gap-1">
            <p className="font-mono text-4xl font-medium tabular-nums text-slate-800">
              {remaining}
            </p>
            <p className="text-xs text-slate-500">{remaining === 1 ? 'second' : 'seconds'}</p>
          </div>

          {/*
            `role="timer"` with a polite live region would announce every tick, which is noise.
            The visible figure carries it for a sighted reader; the sentence below states the
            whole situation once, and the region updates only when the wait ends.
          */}
          <div
            className="h-1.5 overflow-hidden rounded-full bg-slate-100"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={elapsed}
            aria-label="Time until you can sign in again"
          >
            <div
              className="h-full rounded-full bg-brand-500 transition-[width] duration-200 ease-linear"
              style={{ width: `${(elapsed / total) * 100}%` }}
            />
          </div>

          <div className="flex flex-col gap-2 text-center">
            <p className="text-sm font-medium text-slate-800">Too many sign-in attempts</p>
            {/*
              Says what happened, what to do, and that it ends on its own — the last being the
              part the old indefinite lockout could not say, and the reason nobody has to be
              found before this reader can work again.
            */}
            <p className="text-xs text-slate-500">
              Three sign-ins were refused for that email address. This clears itself and the
              sign-in page opens again automatically — your account is not locked and no
              password has changed.
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2">
          <img src={revlvLogo} alt="" aria-hidden="true" className="h-5 w-5 shrink-0" />
          <span className="text-xs text-slate-600">Revlv Solutions Inc.</span>
        </div>
      </div>
    </main>
  );
}
